import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation
    FaTools,            // For Engine & Gearbox Protection
    FaFileInvoiceDollar,// For Return to Invoice
    FaTruckPickup,      // For Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaCarSide,          // For Tyre Cover (FAQ reference)
    FaKey,              // Unused but retained for consistency
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const VolvoCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is Volvo insurance more expensive than other brands?",
      answer: "Yes, slightly, due to higher parts cost and advanced tech features.",
    },
    {
      question: "What does a comprehensive Volvo insurance plan include?",
      answer: "It covers own damage, third-party liability, theft, fire and natural calamities.",
    },
    {
      question: "Can I insure a used Volvo car?",
      answer: "Yes, after a vehicle inspection and verification.",
    },
    {
      question: "Is zero depreciation worth it for a Volvo SUV?",
      answer: "Absolutely, as repairs and panel replacements can be costly.",
    },
    {
      question: "What is RTI cover and is it useful for Volvo cars?",
      answer: "Return to Invoice (RTI) pays full invoice value in case of total loss or theft. Highly recommended for new Volvo buyers.",
    },
    {
      question: "How is the premium for Volvo insurance calculated?",
      answer: "Based on car model, IDV, location, policy type, and past claims.",
    },
    {
      question: "Can I renew Volvo insurance online?",
      answer: "Yes, most insurers offer digital renewal with zero paperwork.",
    },
    {
      question: "Are electric Volvos like the XC40 Recharge covered under standard insurance?",
      answer: "Yes, though some EV-specific coverage may apply.",
    },
    {
      question: "What happens to insurance if I sell my Volvo?",
      answer: "You can transfer the policy or cancel it and request a refund.",
    },
    {
      question: "Does Volvo have a preferred insurance partner?",
      answer: "Volvo dealerships offer insurance, but it’s wise to compare independent options.",
    },
    {
      question: "Is it safe to buy Volvo insurance online?",
      answer: "Yes, but only through verified insurer websites or trusted aggregators.",
    },
    {
      question: "How long does claim settlement usually take?",
      answer: "Typically 7–14 working days depending on claim type and paperwork.",
    },
    {
      question: "What is IDV and how does it impact Volvo insurance?",
      answer: "Insured Declared Value (IDV) affects the premium and maximum claim value.",
    },
    {
      question: "Do insurers offer cashless garages for Volvo?",
      answer: "Yes, most major insurers have tie-ups with authorized Volvo service centers.",
    },
    {
      question: "Can I insure my imported Volvo?",
      answer: "Yes, though it may require specialized or premium plans.",
    },
    {
      question: "Are engine and gearbox covers mandatory for Volvos?",
      answer: "Not mandatory but strongly recommended, especially in flood-prone areas.",
    },
    {
      question: "What’s the fine for driving a Volvo without insurance?",
      answer: "As per Indian law, ₹2,000 for first-time offence and ₹4,000 for repeated violation.",
    },
    {
      question: "Is tyre damage covered in basic Volvo insurance?",
      answer: "Only for accident-related damage. Add Tyre Cover for better protection.",
    },
    {
      question: "What’s the best policy for Volvo XC60 buyers?",
      answer: "A comprehensive plan with zero-dep, engine cover and RSA works best.",
    },
    {
      question: "Can I switch insurers mid-policy?",
      answer: "Typically not, unless you cancel the policy and reapply with valid reason.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description: "Ideal for vehicles with premium panels and electronic components.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine & Gearbox Protection",
      description: "Especially useful for urban drivers and monsoon conditions.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description: "Restores invoice value in case of total loss or theft.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers engine oil, coolant, nuts and bolts often excluded from basic plans.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Offers emergency towing, battery jump-start and other on-road help.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];
const volvoinsurance = [
    {
      name: "XC60",
      path: "/volvo-xc60-car-insurance", 
      image: "/assets/volvoxc60.jpeg",
    },
     {
      name: "XC90",
      path: "/volvo-xc90-car-insurance", 
      image: "/assets/volvoxc90.jpeg",
    },
     {
      name: "XC40",
      path: "/volvo-xc40-car-insurance", 
      image: "/assets/volvoxc40.jpeg",
    },
    {
      name: "C40",
      path: "/volvo-c40-car-insurance", 
      image: "/assets/volvoc40.jpeg",
    },
   
     {
      name: "S90",
      path: "/volvo-s90-car-insurance", 
      image: "/assets/volvos90.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>Volvo Car Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Volvo car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Volvo vehicle."
        />
        <meta
          name="keywords"
          content="volvo car insurance, luxury car insurance india, volvo xc60 insurance, volvo xc40 recharge insurance, volvo s90 insurance, car insurance quotes"
        />
           <link
          rel="canonical"
          href="https://thefinmaster.com/volvo-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Volvo Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Volvo Car Insurance</span>
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Volvo is celebrated for its Scandinavian design, advanced safety features, and reliable performance. Whether you drive a sleek S60 or a spacious XC90, safeguarding your car begins with the right insurance coverage. This guide walks you through everything you need to know about insuring a Volvo, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/volvoinsurance.jpeg"
                alt="Volvo Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Volvo in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Volvo Cars entered the Indian market in 2007 and has built a loyal customer base among premium and safety-conscious buyers. The company offers a curated lineup of vehicles that combine cutting-edge technology with minimalist luxury. Volvo India operates through a network of premium dealerships and service centers across major cities.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Popular Volvo models like the XC40, XC60, S90 and XC90 offer advanced driver assistance features, hybrid technology and premium interiors. Insurance plays a key role in safeguarding these high-value vehicles from unexpected losses or damage.
          </p>
        </div>
      </div>
        <div className="p-4 sm:p-8 bg-white">
                          <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                            Choose Your Car Brand
                          </h2>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {volvoinsurance.map((calc, index) => (
                              <motion.div
                                key={index}
                                whileHover={{ scale: 1.04 }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                              >
                                <Link
                                  to={calc.path}
                                  className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                >
                                  <img
                                    src={calc.image}
                                    alt={calc.name}
                                    className="w-16 h-16 object-contain mb-2"
                                  />
                                  <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                    {calc.name}
                                  </span>
                                </Link>
                              </motion.div>
                            ))}
                          </div>
                        </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Volvo Owners Need the Right Insurance Plan
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Volvo vehicles come equipped with top-tier safety systems, advanced electronics and luxurious materials. These features, while enhancing the driving experience, also make repairs expensive if accidents or damages occur. A comprehensive insurance plan helps protect you from:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>On-road collisions and accidents</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft and attempted theft</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Natural disasters such as floods, landslides and earthquakes</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party liabilities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>High costs for genuine spare parts and repairs</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            In addition, premium insurance plans for Volvo cars often include access to cashless garages, extended coverage benefits and quick claims support.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Volvo Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here's a snapshot of some popular Volvo models available in India along with approximate Insured Declared Values (IDV) and estimated starting premiums:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "XC40 Recharge", category: "Electric SUV", idv: "60–65 Lakh", premium: "₹45,000 to ₹58,000" },
              { model: "XC60", category: "Luxury SUV", idv: "68–74 Lakh", premium: "₹48,000 to ₹60,000" },
              { model: "XC90", category: "3-row Premium SUV", idv: "95 Lakh–1.05 Cr", premium: "₹65,000 to ₹82,000" },
              { model: "S90", category: "Luxury Sedan", idv: "72–80 Lakh", premium: "₹50,000 to ₹64,000" },
              { model: "C40 Recharge", category: "Coupe EV SUV", idv: "62–68 Lakh", premium: "₹47,000 to ₹59,000" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Model</th>
                  <th className="p-3 border border-gray-300 text-left">Category</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["XC40 Recharge", "Electric SUV", "60–65 Lakh", "₹45,000 to ₹58,000"],
                  ["XC60", "Luxury SUV", "68–74 Lakh", "₹48,000 to ₹60,000"],
                  ["XC90", "3-row Premium SUV", "95 Lakh–1.05 Cr", "₹65,000 to ₹82,000"],
                  ["S90", "Luxury Sedan", "72–80 Lakh", "₹50,000 to ₹64,000"],
                  ["C40 Recharge", "Coupe EV SUV", "62–68 Lakh", "₹47,000 to ₹59,000"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for Volvo Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Given the high-tech features and luxury components, it is advisable to consider these add-ons when insuring a Volvo:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Volvo Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here are some insurers preferred by Volvo car owners for their strong service quality and premium coverage options:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">HDFC ERGO</h3>
            <p className="text-gray-700 text-sm mt-1">Offers tailored plans with zero-dep and fast claims.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ICICI Lombard</h3>
            <p className="text-gray-700 text-sm mt-1">Suitable for luxury car insurance with customizable features.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Tata AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Competitive pricing with good garage network.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Bajaj Allianz</h3>
            <p className="text-gray-700 text-sm mt-1">Quick digital issuance and strong customer service.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Digit Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Known for transparent digital claims and ease of use.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Volvo Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Volvo car insurance can be optimized with the right approach. Here are some cost-saving tips:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare quotes from multiple insurers before buying or renewing.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Use accumulated No Claim Bonus (NCB).</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Opt for a voluntary deductible to reduce premiums.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install certified anti-theft devices for added discounts.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Maintain a clean driving record to qualify for better rates.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Volvo Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              You’ll need these documents for buying or renewing your Volvo’s insurance:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance copy (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Aadhaar or PAN card</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Recent passport-sized photograph (in some cases)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Volvo Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Owning a Volvo is a statement of class, safety and technology. Protecting it with a well-thought-out insurance plan ensures you enjoy a worry-free driving experience. Whether it’s the powerful XC90 or the eco-conscious XC40 Recharge, the right coverage helps reduce repair expenses and ensures quick recovery after any mishap.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we simplify your insurance journey by offering expert insights, comparison tools and curated plans tailored to premium cars like Volvo.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default VolvoCarInsurance;